import React, { useState, useContext, useEffect, useRef } from 'react';
import {
	Box,
	Button,
	TextField,
	Typography,
	InputAdornment,
} from '@mui/material';
import { addDays } from 'date-fns';
import Modal from './Modal';
import StripeFeeQuoteHelper from '../StripeFeeQuoteHelper';
import LineItemsEditor from '../LineItems/LineItemsEditor';
import BidFormatToggle from '../LineItems/BidFormatToggle';
import { makeApiRequest } from '../../Shared/Api';
import { refreshOnboardingChecklist } from '../../Shared/onboarding';
import { useSnackbar } from 'notistack';
import { JobDetailContext } from '../../Pages/Jobs/context/JobDetailContext';
import { useStripePaymentsEnabled } from '../../Context/AppContext';
import { useDefaultItemizedBids } from '../../Hooks/useFeature';
import {
	createDraftLineItem,
	resolveItemizedFromJob,
	serializeLineItemsForApi,
} from '../../Shared/lineItems';
import { toDatetimeLocalValue } from '../../Shared/datetime';

const CreateBidModal = ({ open, onClose, jobId, onJobUpdated }) => {
	const [amount, setAmount] = useState('');
	const [useItemizedBid, setUseItemizedBid] = useState(false);
	const [quoteIncludesStripeFee, setQuoteIncludesStripeFee] = useState(false);
	const [expiresAtLocal, setExpiresAtLocal] = useState('');
	const [depositPercent, setDepositPercent] = useState('');
	const [loading, setLoading] = useState(false);
	const [lineItemsDraft, setLineItemsDraft] = useState([]);
	const [lineItemsForFee, setLineItemsForFee] = useState([]);
	const [editorSeed, setEditorSeed] = useState(0);
	const [initialLineItems, setInitialLineItems] = useState([]);
	const lineItemsTableRef = useRef(null);
	const { enqueueSnackbar } = useSnackbar();
	const { state } = useContext(JobDetailContext);
	const { selectedJob } = state;
	const stripePaymentsEnabled = useStripePaymentsEnabled();
	const defaultItemizedBids = useDefaultItemizedBids();
	const showingItemized = useItemizedBid;

	useEffect(() => {
		if (!open) return;

		const defaultExpiry = toDatetimeLocalValue(addDays(new Date(), 14));
		setExpiresAtLocal(
			selectedJob?.bid_details?.expires_at
				? toDatetimeLocalValue(new Date(selectedJob.bid_details.expires_at))
				: defaultExpiry,
		);
		setQuoteIncludesStripeFee(
			Boolean(selectedJob?.bid_details?.quote_includes_stripe_fee),
		);
		setLineItemsForFee([]);
		const prior = selectedJob?.bid_details?.line_items || [];
		setInitialLineItems(
			prior.length
				? prior.map((item) => ({
						...createDraftLineItem(),
						description: item.description || '',
						quantity: item.quantity || 1,
						unit_price: item.unit_price ?? '',
					}))
				: [createDraftLineItem()],
		);
		setEditorSeed((n) => n + 1);
		setLineItemsDraft([]);
		setUseItemizedBid(
			resolveItemizedFromJob(selectedJob, { defaultItemizedBids }),
		);
		setAmount(
			Number(selectedJob?.bid_amount) > 0 ? String(selectedJob.bid_amount) : '',
		);
		setDepositPercent(
			selectedJob?.bid_details?.deposit_percent
				? String(selectedJob.bid_details.deposit_percent)
				: '',
		);
	}, [open, selectedJob, defaultItemizedBids]);

	const handleSubmit = async () => {
		let serializedLineItems = [];
		if (showingItemized) {
			const flushed =
				(await lineItemsTableRef.current?.flushPendingSaves?.()) ||
				lineItemsDraft;
			serializedLineItems = serializeLineItemsForApi(flushed);
			const total = serializedLineItems.reduce(
				(sum, item) =>
					sum + (parseFloat(item.quantity) * parseFloat(item.unit_price) || 0),
				0,
			);
			if (!serializedLineItems.length || total <= 0) {
				enqueueSnackbar(
					'Add at least one line item with a price before sending a bid.',
					{ variant: 'error' },
				);
				return;
			}
		} else if (
			!amount ||
			Number.isNaN(Number(amount)) ||
			parseFloat(amount) <= 0
		) {
			enqueueSnackbar('Please enter a valid bid amount', { variant: 'error' });
			return;
		}

		if (!expiresAtLocal) {
			enqueueSnackbar('Choose a bid expiration date', { variant: 'warning' });
			return;
		}

		setLoading(true);
		try {
			const payload = {
				job: jobId,
				reason: 'new_job',
				send_bid: true,
				expires_at: new Date(expiresAtLocal).toISOString(),
				quote_includes_stripe_fee: false,
			};

			if (showingItemized) {
				payload.itemized_pricing = true;
				payload.line_items = serializedLineItems;
			} else {
				payload.amount = parseFloat(amount);
				payload.itemized_pricing = false;
			}
			if (stripePaymentsEnabled) {
				payload.quote_includes_stripe_fee = quoteIncludesStripeFee;
			}
			if (depositPercent) {
				payload.deposit_percent = Number(depositPercent);
			}

			await makeApiRequest('POST', '/bids/', payload);

			if (typeof onJobUpdated === 'function') {
				await onJobUpdated();
			}

			refreshOnboardingChecklist();
			enqueueSnackbar('Bid proposal sent to customer', { variant: 'success' });
			onClose();
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to send bid', {
				variant: 'error',
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			modalTitle="Create Bid"
			maxWidth={showingItemized ? 'md' : 'sm'}
			alwaysMaxWidth
			useActionButtons
			actionButtons={
				<Box sx={{ display: 'flex', gap: 1 }}>
					<Button onClick={onClose} disabled={loading}>
						Cancel
					</Button>
					<Button variant="contained" onClick={handleSubmit} disabled={loading}>
						{loading ? 'Sending...' : 'Send Proposal'}
					</Button>
				</Box>
			}
		>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
				<Typography variant="body2" color="text.secondary">
					{showingItemized
						? 'Add your line items below, then send the customer a branded proposal with an itemized PDF.'
						: 'Enter the project total and send the customer a branded proposal PDF.'}
				</Typography>

				<BidFormatToggle
					value={useItemizedBid}
					onChange={setUseItemizedBid}
					disabled={loading}
				/>

				{showingItemized ? (
					<Box>
						<LineItemsEditor
							key={`create-bid-items-${editorSeed}`}
							ref={lineItemsTableRef}
							initialItems={initialLineItems}
							onItemsChange={(items) => {
								setLineItemsDraft(items);
								setLineItemsForFee(items);
							}}
							compact
							quoteIncludesStripeFee={
								stripePaymentsEnabled && quoteIncludesStripeFee
							}
						/>
						{stripePaymentsEnabled ? (
							<Box sx={{ mt: 2 }}>
								<StripeFeeQuoteHelper
									lineItems={lineItemsForFee}
									includesFee={quoteIncludesStripeFee}
									disabled={loading}
									onApplyFee={() => {}}
									onIncludesFeeChange={setQuoteIncludesStripeFee}
								/>
							</Box>
						) : null}
					</Box>
				) : (
					<>
						<TextField
							label="Bid Amount"
							fullWidth
							required
							type="text"
							inputMode="decimal"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							onBlur={(e) => {
								const parsed = parseFloat(e.target.value);
								if (!Number.isNaN(parsed) && parsed > 0) {
									setAmount(parsed.toFixed(2));
								}
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">$</InputAdornment>
								),
							}}
							helperText="One project total on the customer proposal PDF."
						/>

						{stripePaymentsEnabled ? (
							<StripeFeeQuoteHelper
								amount={amount}
								includesFee={quoteIncludesStripeFee}
								disabled={loading}
								onApplyFee={(grossAmount) => {
									setAmount(Number(grossAmount).toFixed(2));
									setQuoteIncludesStripeFee(true);
								}}
								onIncludesFeeChange={setQuoteIncludesStripeFee}
							/>
						) : null}
					</>
				)}

				<TextField
					label="Proposal valid until"
					type="datetime-local"
					fullWidth
					required
					value={expiresAtLocal}
					onChange={(e) => setExpiresAtLocal(e.target.value)}
					slotProps={{ inputLabel: { shrink: true } }}
					helperText="The customer can accept or decline until this date."
				/>

				<TextField
					label="Deposit required on acceptance"
					type="number"
					fullWidth
					value={depositPercent}
					onChange={(e) => setDepositPercent(e.target.value)}
					disabled={loading}
					slotProps={{
						input: {
							endAdornment: <InputAdornment position="end">%</InputAdornment>,
							inputProps: { min: 1, max: 100, step: 1 },
						},
					}}
					helperText="Optional. A deposit payment link is emailed when the customer accepts."
				/>
			</Box>
		</Modal>
	);
};

export default CreateBidModal;
